import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useDocsRoute } from './docs-route-context';

interface Props {
  href?: string;
  children?: ReactNode;
}

function resolveRelative(currentRole: string, href: string): string {
  // Strip trailing .md, preserve #anchor
  const [rawPath, hash] = href.split('#');
  let segments: string[] = [currentRole];
  // current page is the "file" — siblings resolve against its directory
  if (rawPath.startsWith('./')) {
    segments = [currentRole, rawPath.slice(2)];
  } else if (rawPath.startsWith('../')) {
    // walk up from the role folder
    const parts = rawPath.split('/');
    const ups: string[] = [];
    let i = 0;
    while (parts[i] === '..') {
      ups.push('..');
      i++;
    }
    const rest = parts.slice(i).join('/');
    // From /docs/{role}/{page}, one ".." lands at /docs/{role} (i.e. role root),
    // so ../admin/foo.md → /docs/admin/foo. Two ".." would land at /docs, rare.
    if (ups.length === 1) {
      segments = ['', ...rest.split('/')];
    } else {
      segments = rest.split('/');
    }
  } else {
    segments = [currentRole, rawPath];
  }
  let joined = segments.filter((s) => s !== '').join('/').replace(/\.md$/, '');
  joined = `/docs/${joined}`.replace(/\/+/g, '/');
  return hash ? `${joined}#${hash}` : joined;
}

export function DocsLink({ href, children }: Props) {
  const { role } = useDocsRoute();
  const target = href ?? '';

  if (!target) {
    return <span>{children}</span>;
  }

  // External link
  if (/^(https?:|mailto:|tel:)/.test(target)) {
    return (
      <a href={target} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  // Same-page anchor
  if (target.startsWith('#')) {
    return <a href={target}>{children}</a>;
  }

  // Absolute /docs/* link
  if (target.startsWith('/docs')) {
    const clean = target.replace(/\.md(#|$)/, '$1');
    return <Link to={clean}>{children}</Link>;
  }

  // Absolute in-app link (e.g. /login) — treat as react-router navigation
  if (target.startsWith('/')) {
    return <Link to={target}>{children}</Link>;
  }

  // Relative markdown link (./foo.md, ../admin/bar.md#baz)
  const resolved = resolveRelative(role, target);
  return <Link to={resolved}>{children}</Link>;
}
